import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getGraphQLClient } from '@/common/graphqlClient';
import { QueryKeys } from '@/common/queryKeys';
import { graphql } from '@/generated/graphql';
import type {
  DeleteDriverHolidayMutationVariables,
  GetDriverHolidaysQueryVariables,
  InsertDriverHolidayMutationVariables,
} from '@/generated/graphql/graphql';

const GET_DRIVER_HOLIDAYS = graphql(`
  query GetDriverHolidays($id: String!) {
    user_by_pk(id: $id) {
      driver_holidays {
        forced
        date
        id
      }
    }
  }
`);

const INSERT_DRIVER_HOLIDAY = graphql(`
  mutation InsertDriverHoliday($forced: Boolean!, $driverId: String!, $date: timestamptz!) {
    insert_driver_holiday_one(object: { date: $date, driverId: $driverId, forced: $forced }) {
      id
    }
  }
`);

const DELETE_DRIVER_HOLIDAY = graphql(`
  mutation DeleteDriverHoliday($id: Int!) {
    delete_driver_holiday_by_pk(id: $id) {
      id
      driverId
    }
  }
`);

export const useGetDriverHolidays = (variables: GetDriverHolidaysQueryVariables) => {
  const client = getGraphQLClient();

  return useQuery({
    queryKey: [QueryKeys.GET_DRIVER_HOLIDAYS, variables.id],
    queryFn: () => client.request(GET_DRIVER_HOLIDAYS, variables),
    enabled: Boolean(variables.id),
  });
};

export const useInsertDriverHoliday = () => {
  const client = getGraphQLClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: InsertDriverHolidayMutationVariables) =>
      client.request(INSERT_DRIVER_HOLIDAY, variables),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_DRIVER_HOLIDAYS, variables.driverId],
      });
    },
  });
};

export const useDeleteDriverHoliday = () => {
  const client = getGraphQLClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: DeleteDriverHolidayMutationVariables) =>
      client.request(DELETE_DRIVER_HOLIDAY, variables),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_DRIVER_HOLIDAYS, data.delete_driver_holiday_by_pk?.driverId],
      });
    },
  });
};
